import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { ArrowLeft, Plus, Trash2, Pencil, Zap, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const api = axios.create({ baseURL: process.env.REACT_APP_BACKEND_URL + '/api' });
api.interceptors.request.use((c) => {
  const t = localStorage.getItem('token');
  if (t) c.headers.Authorization = `Bearer ${t}`;
  return c;
});

const emptyForm = { shortcut: '/', title: '', body: '' };

const CRMQuickRepliesPage = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [openEdit, setOpenEdit] = useState(false);
  const [editing, setEditing] = useState(null); // reply object or null
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  // Auto-reply settings state (Admin only)
  const [autoOpen, setAutoOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [autoForm, setAutoForm] = useState({ enabled: false, template_name: '', template_language: 'en' });
  const [savingAuto, setSavingAuto] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, cfg] = await Promise.all([
        api.get('/crm/quick-replies'),
        api.get('/crm/config').catch(() => ({ data: null })),
      ]);
      setRows(r.data.rows || []);
      setConfig(cfg?.data || null);
      if (cfg?.data) {
        setAutoForm({
          enabled: !!cfg.data.auto_reply_enabled,
          template_name: cfg.data.auto_reply_template_name || '',
          template_language: cfg.data.auto_reply_template_language || 'en',
        });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpenEdit(true);
  };

  const openExisting = (r) => {
    setEditing(r);
    setForm({ shortcut: r.shortcut, title: r.title, body: r.body });
    setOpenEdit(true);
  };

  const save = async () => {
    if (!/^\/[a-z0-9_-]{1,32}$/.test(form.shortcut)) {
      toast.error('Shortcut must start with / and be lowercase letters/digits/_/-'); return;
    }
    if (!form.title.trim() || !form.body.trim()) {
      toast.error('Title and body are required'); return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/crm/quick-replies/${editing.id}`, form);
        toast.success('Reply updated');
      } else {
        await api.post('/crm/quick-replies', form);
        toast.success('Reply created');
      }
      setOpenEdit(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const del = async (r) => {
    if (!window.confirm(`Delete "${r.title}"?`)) return;
    try {
      await api.delete(`/crm/quick-replies/${r.id}`);
      toast.success('Deleted');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  const openAutoDialog = async () => {
    setAutoOpen(true);
    try {
      const t = await api.get('/crm/templates', { params: { status: 'APPROVED' } });
      setTemplates(t.data.data || []);
    } catch { setTemplates([]); }
  };

  const saveAuto = async () => {
    setSavingAuto(true);
    try {
      await api.put('/crm/auto-reply', autoForm);
      toast.success('Auto-reply saved');
      setAutoOpen(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setSavingAuto(false); }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-4 md:p-6 max-w-4xl mx-auto space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/crm')} data-testid="qr-back">
              <ArrowLeft className="w-4 h-4 mr-1" /> Inbox
            </Button>
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Zap className="w-5 h-5 text-emerald-600" /> Quick Replies
              </h1>
              <p className="text-xs md:text-sm text-gray-500">Canned replies your team can insert with a slash shortcut in the CRM inbox.</p>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={load} data-testid="qr-refresh"><RefreshCw className="w-4 h-4" /></Button>
            {user?.role === 'Admin' && (
              <Button variant="outline" size="sm" onClick={openAutoDialog} data-testid="qr-auto-btn">
                Auto first-reply {config?.auto_reply_enabled && <Badge className="ml-1 bg-emerald-500 text-white">ON</Badge>}
              </Button>
            )}
            <Button onClick={openNew} className="bg-emerald-600 hover:bg-emerald-700" data-testid="qr-add-btn">
              <Plus className="w-4 h-4 mr-1" /> New reply
            </Button>
          </div>
        </div>

        <Card>
          <CardContent className="p-0 divide-y" data-testid="qr-list">
            {loading && <div className="p-6 text-center text-sm text-gray-400"><RefreshCw className="w-4 h-4 animate-spin inline mr-1" /> Loading…</div>}
            {!loading && rows.length === 0 && (
              <div className="p-8 text-center text-sm text-gray-400" data-testid="qr-empty">
                <Zap className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                No quick replies yet. Add one and it will show up in the CRM composer via <span className="font-mono bg-gray-100 px-1 rounded">/shortcut</span>.
              </div>
            )}
            {rows.map((r) => (
              <div key={r.id} className="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3" data-testid={`qr-row-${r.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <code className="bg-emerald-50 text-emerald-800 px-1.5 py-0.5 rounded text-xs font-mono">{r.shortcut}</code>
                    <p className="font-semibold text-gray-900 text-sm">{r.title}</p>
                  </div>
                  <p className="text-xs text-gray-600 mt-1 whitespace-pre-wrap line-clamp-3">{r.body}</p>
                  <p className="text-[10px] text-gray-400 mt-1">Created by {r.created_by_user_name || '—'}</p>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => openExisting(r)} data-testid={`qr-edit-${r.id}`}><Pencil className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => del(r)} className="text-red-600 hover:bg-red-50" data-testid={`qr-del-${r.id}`}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Create / edit dialog */}
      <Dialog open={openEdit} onOpenChange={setOpenEdit}>
        <DialogContent data-testid="qr-edit-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit quick reply' : 'New quick reply'}</DialogTitle>
            <DialogDescription>
              Type <code>/{'{shortcut}'}</code> in the inbox composer to insert this reply.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="text-sm font-medium">Shortcut</label>
              <Input value={form.shortcut} onChange={(e) => setForm({ ...form, shortcut: e.target.value.toLowerCase() })}
                placeholder="/hi" data-testid="qr-form-shortcut" />
              <p className="text-[11px] text-gray-500 mt-0.5">Starts with <span className="font-mono">/</span>, lowercase letters/digits/_/-, up to 32 chars.</p>
            </div>
            <div>
              <label className="text-sm font-medium">Title</label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Greeting" data-testid="qr-form-title" />
            </div>
            <div>
              <label className="text-sm font-medium">Body</label>
              <Textarea rows={5} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })}
                placeholder="Hi! Thanks for reaching out to Eden Skin Clinic. A team member will respond within 15 minutes."
                data-testid="qr-form-body" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenEdit(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="qr-form-save">
              {saving ? 'Saving…' : (editing ? 'Save' : 'Create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Auto-reply dialog */}
      <Dialog open={autoOpen} onOpenChange={setAutoOpen}>
        <DialogContent data-testid="qr-auto-dialog">
          <DialogHeader>
            <DialogTitle>Auto first-reply</DialogTitle>
            <DialogDescription>
              When someone new messages you on WhatsApp, automatically send an approved template so they get an instant acknowledgement.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={autoForm.enabled}
                onChange={(e) => setAutoForm({ ...autoForm, enabled: e.target.checked })}
                data-testid="qr-auto-enabled" />
              <span className="text-sm">Enable auto first-reply</span>
            </label>
            <div>
              <label className="text-sm font-medium">Template</label>
              <select value={autoForm.template_name} onChange={(e) => setAutoForm({ ...autoForm, template_name: e.target.value })}
                className="w-full h-9 border rounded-md px-2 mt-1 disabled:opacity-50" disabled={!autoForm.enabled}
                data-testid="qr-auto-template">
                <option value="">— Pick a template —</option>
                {templates.map((t) => <option key={t.id || t.name} value={t.name}>{t.name} ({t.language})</option>)}
              </select>
              <p className="text-[11px] text-gray-500 mt-1">Must be an <b>APPROVED</b> Meta template. Manage templates under CRM → Meta Templates.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAutoOpen(false)}>Cancel</Button>
            <Button onClick={saveAuto} disabled={savingAuto} className="bg-emerald-600 hover:bg-emerald-700" data-testid="qr-auto-save">
              {savingAuto ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default CRMQuickRepliesPage;
